import type { CompetitiveConfig } from "./config";
import {
  cloneDeath,
  cloneFood,
  cloneSnake,
  turnRateForLength,
} from "./match-entities";
import { inspectSpawnPoint } from "./spawn";
import { sampleTrail } from "./trail";
import type {
  BodySegment,
  DeathRecord,
  Food,
  MatchDebugSnapshot,
  MatchMode,
  MatchPhase,
  MatchRanking,
  MatchResult,
  MatchSnapshot,
  SnakeState,
} from "./types";
import type { Vec2 } from "./math";

interface SnapshotInput {
  readonly phase: MatchPhase;
  readonly readyRemainingSeconds: number;
  readonly mode: MatchMode;
  readonly assistEnabled: boolean;
  readonly assisted: boolean;
  readonly elapsedSeconds: number;
  readonly matchDurationSeconds: number;
  readonly snakes: readonly SnakeState[];
  readonly foods: readonly Food[];
  readonly deaths: readonly DeathRecord[];
  readonly rankings: readonly MatchRanking[];
  readonly respawnRemaining: (snakeId: string) => number;
  readonly protectionRemaining: (snakeId: string) => number;
}

export const buildMatchSnapshot = (input: SnapshotInput): MatchSnapshot => ({
  phase: input.phase,
  readyRemainingSeconds: input.readyRemainingSeconds,
  mode: input.mode,
  assistEnabled: input.assistEnabled,
  assisted: input.assisted,
  elapsedSeconds: input.elapsedSeconds,
  remainingSeconds: Math.max(
    0,
    input.matchDurationSeconds - input.elapsedSeconds,
  ),
  snakes: input.snakes.map((snake) => ({
    ...cloneSnake(snake),
    respawnRemainingSeconds: input.respawnRemaining(snake.id),
    protectionRemainingSeconds: input.protectionRemaining(snake.id),
  })),
  foods: input.foods.map(cloneFood),
  deaths: input.deaths.map(cloneDeath),
  rankings: input.rankings.map((ranking) => ({ ...ranking })),
});

export const buildRankings = (
  snakes: readonly SnakeState[],
  includeDead: boolean,
): MatchRanking[] =>
  snakes
    .filter((snake) => includeDead || snake.alive)
    .sort((left, right) =>
      includeDead
        ? Number(right.alive) - Number(left.alive) ||
          right.length - left.length ||
          left.id.localeCompare(right.id)
        : right.length - left.length || left.id.localeCompare(right.id),
    )
    .map((snake, index) => ({
      snakeId: snake.id,
      rank: index + 1,
      length: snake.length,
      alive: snake.alive,
    }));

export interface MatchStatsView {
  readonly maxLength: number;
  readonly foodCollected: number;
  readonly eliminations: number;
  readonly deaths: number;
  readonly aliveSeconds: number;
}

export const buildMatchResult = (
  snake: SnakeState,
  stats: MatchStatsView,
  deaths: readonly DeathRecord[],
  resultRankings: readonly MatchRanking[],
  elapsedSeconds: number,
  mode: MatchMode,
  assisted: boolean,
): MatchResult => {
  const death = deaths.find((candidate) => candidate.snakeId === snake.id);
  const survivalSeconds = death?.atSeconds ?? elapsedSeconds;
  const finalRank =
    resultRankings.find((ranking) => ranking.snakeId === snake.id)?.rank ?? 1;
  return {
    snakeId: snake.id,
    maxLength: stats.maxLength,
    foodCollected: stats.foodCollected,
    eliminations: stats.eliminations,
    survivalMs: Math.round(survivalSeconds * 1_000),
    finalRank,
    score: Math.round(
      stats.maxLength +
        stats.foodCollected * 5 +
        stats.eliminations * 100 +
        survivalSeconds * 10,
    ),
    mode,
    assisted,
    deaths: stats.deaths,
    sessionMs: Math.round(elapsedSeconds * 1_000),
    aliveMs: Math.round(stats.aliveSeconds * 1_000),
    currentLength: snake.length,
  };
};

interface DebugSnapshotInput {
  readonly stepIndex: number;
  readonly botCount: number;
  readonly spatialBucketCount: number;
  readonly snakes: readonly SnakeState[];
  readonly bodySegments: readonly BodySegment[];
  readonly deaths: readonly DeathRecord[];
  readonly playerSpawn: Vec2 | null;
  readonly config: CompetitiveConfig;
  readonly foods: readonly Food[];
  readonly elapsedSeconds: number;
  readonly tideRadius: number;
}

export const buildMatchDebugSnapshot = (
  input: DebugSnapshotInput,
): Omit<MatchDebugSnapshot, "interpolationAlpha"> => {
  const player = input.snakes.find((snake) => snake.controller === "human");
  return {
    simulatedStep: input.stepIndex,
    botCount: input.botCount,
    spatialBucketCount: input.spatialBucketCount,
    snakes: input.snakes.map((snake) => ({
      id: snake.id,
      alive: snake.alive,
      head: { ...snake.position },
      headRadius: snake.width * input.config.headRadiusFactor,
      bodySamples: sampleTrail(snake.trail, Math.max(2, snake.width / 2)),
      boosting: snake.boosting,
      targetHeading: snake.targetHeading,
      turnRate: turnRateForLength(snake.length, input.config, snake.speed),
    })),
    bodySegments: input.bodySegments.map((segment) => ({
      ...segment,
      start: { ...segment.start },
      end: { ...segment.end },
    })),
    deaths: input.deaths.map(cloneDeath),
    spawn:
      player && input.playerSpawn
        ? inspectSpawnPoint(
            input.playerSpawn,
            input.snakes.filter((snake) => snake.id !== player.id),
            input.config.arenaRadius,
          )
        : null,
    tide: {
      radius: input.tideRadius,
      resonantFoodIds: input.foods
        .filter(
          (food) =>
            food.source === "natural" &&
            food.resonantUntil > input.elapsedSeconds,
        )
        .map((food) => food.id),
    },
  };
};
